import type { PhaseChangeLine } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaPencilAlt } from 'react-icons/fa';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import type { UseTimeseriesChartReturn } from '../../../participantData/hooks/useTimeseriesChart';
import { PhaseChangeLineForm } from './PhaseChangeLineForm';

type FormValues = PhaseChangeLine;

interface PhaseChangeLineEditFormProps {
  chart: UseTimeseriesChartReturn;
  phaseChangeLine: PhaseChangeLine;
  onClose: () => void;
}

const PhaseChangeLineEditForm = (props: PhaseChangeLineEditFormProps) => {
  const { chart, phaseChangeLine, onClose } = props;

  const methods = useForm({
    defaultValues: phaseChangeLine,
  });

  const onSubmit = (values: FormValues) => {
    chart.onEditPhaseChangeLine(values.id, values);
    onClose();
  };

  return (
    <ChakraV3.Stack
      bg='white'
      gap={4}
      w='md'
      layerStyle='outline.subtle'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection
          as='form'
          heading='Edit phase change line'
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <ChakraV3.Separator />
          <PhaseChangeLineForm />
          <HookedSubmitButton w='full' size='sm'>
            Save
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  chart: UseTimeseriesChartReturn;
  phaseChangeLine: PhaseChangeLine;
}

export const PhaseChangeLineEditControl = (props: Props) => {
  const { chart, phaseChangeLine } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      placement='right-end'
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => (
        <PhaseChangeLineEditForm
          chart={chart}
          phaseChangeLine={phaseChangeLine}
          onClose={onClose}
        />
      )}
    >
      <ChakraV3.IconButton aria-label='Edit' size='2xs' variant='ghost'>
        <FaPencilAlt />
      </ChakraV3.IconButton>
    </FloatingPopover>
  );
};
